import { useCallback, useEffect, useState } from "react";
import type { MessengerPlatform } from "@carry/messenger-platform";
import { Button, cn } from "@carry/ui";
import { DriverApp } from "./App";
import { PendingScreen } from "./PendingScreen";
import {
  clearStoredDriverSession,
  isDriverAppRole,
  readStoredDriverSession,
  signInWithMessenger,
  verifyDriverSession,
  type StoredDriverSession,
} from "./auth";

interface DriverShellProps {
  platform: MessengerPlatform;
  apiBaseUrl: string;
  /**
   * E2E モード。true のとき、フィクスチャが localStorage セッションと Cookie を注入済みである
   * 前提で、再サインイン (LINE 検証を通らないモックトークン) をスキップし既存セッションを使う。
   * 実機 (false) では Cookie 認証のため起動毎に必ずサインインして Cookie を発行する。
   */
  e2e?: boolean;
}

type ShellState =
  | { kind: "loading" }
  | { kind: "active"; session: StoredDriverSession }
  | { kind: "pending"; session: StoredDriverSession }
  | { kind: "error"; message: string };

type ErrorKind = "network" | "rate_limited" | "auth" | "unknown";

function classifyError(message: string): ErrorKind {
  const m = message.toLowerCase();
  if (m.includes("rate") || m.includes("短時間")) return "rate_limited";
  if (m.includes("fetch") || m.includes("network") || m.includes("通信") || m.includes("offline"))
    return "network";
  if (
    m.includes("unauth") ||
    m.includes("invalid") ||
    m.includes("token") ||
    m.includes("driver") ||
    m.includes("権限")
  )
    return "auth";
  return "unknown";
}

function isActiveDriverSession(session: StoredDriverSession): boolean {
  return session.user.status === "active" && isDriverAppRole(session.user.role);
}

function driverRoleRequiredMessage() {
  return "ドライバー権限が必要です。管理者にお問い合わせください。";
}

export function DriverShell({ platform, apiBaseUrl, e2e = false }: DriverShellProps) {
  const [state, setState] = useState<ShellState>({ kind: "loading" });
  const [retryCount, setRetryCount] = useState(0);

  const establish = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      // 既存の active セッションがあれば、有効な Cookie が残っているか確認し、あれば再サインインを
      // 省略して即 active にする (読込短縮)。
      //  - E2E: フィクスチャが Cookie + localStorage を注入済みなので検証不要で即 active
      //    (モックトークンは LINE 検証を通らず再サインインできない)。
      //  - 実機: 軽量な auth/me で Cookie の有効性を確認。有効なら sign-in (LINE 検証 + セッション
      //    発行) を省略。無効/未発行なら下のサインインへ進む (httpOnly Cookie は JS から存在確認
      //    できず ITP で消えることもあるため、localStorage だけでは active と断定できない)。
      const existing = readStoredDriverSession();
      if (existing && isActiveDriverSession(existing)) {
        if (e2e || (await verifyDriverSession({ apiBaseUrl }))) {
          setState({ kind: "active", session: existing });
          return;
        }
      }
      clearStoredDriverSession();
      const result = await signInWithMessenger({ platform, apiBaseUrl });
      if (!result.ok) {
        setState({
          kind: "error",
          message:
            result.message ??
            (result.error === "RATE_LIMITED"
              ? "短時間に多くのサインインがありました。1 分ほど時間を置いてください。"
              : `サインインに失敗しました (${result.error})`),
        });
        return;
      }
      if (!isDriverAppRole(result.user.role)) {
        clearStoredDriverSession();
        setState({ kind: "error", message: driverRoleRequiredMessage() });
        return;
      }
      const session: StoredDriverSession = {
        expiresAt: result.expiresAt,
        user: result.user,
      };
      setState({ kind: result.user.status === "active" ? "active" : "pending", session });
      setRetryCount(0);
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [platform, apiBaseUrl, e2e]);

  useEffect(() => {
    void establish();
  }, [establish]);

  if (state.kind === "loading") {
    return <LoadingScreen />;
  }

  if (state.kind === "error") {
    return (
      <ErrorScreen
        message={state.message}
        retryCount={retryCount}
        onRetry={() => {
          setRetryCount((n) => n + 1);
          void establish();
        }}
      />
    );
  }

  if (state.kind === "pending") {
    return (
      <PendingScreen
        userName={state.session.user.name}
        apiBaseUrl={apiBaseUrl}
        onRedeemed={() => {
          clearStoredDriverSession();
          return establish();
        }}
      />
    );
  }

  return <DriverApp platform={platform} apiBaseUrl={apiBaseUrl} session={state.session} />;
}

function ErrorScreen({
  message,
  retryCount,
  onRetry,
}: {
  message: string;
  retryCount: number;
  onRetry: () => void;
}) {
  const kind = classifyError(message);
  const heading =
    kind === "rate_limited"
      ? "リクエストが多すぎます"
      : kind === "network"
        ? "通信に失敗しました"
        : kind === "auth"
          ? "認証に失敗しました"
          : "ログインに失敗しました";
  const subhint =
    kind === "rate_limited"
      ? "1 分ほど経ってから再試行してください。"
      : kind === "network"
        ? "電波状況を確認してから再試行してください。"
        : kind === "auth"
          ? "LINE アカウントの権限を確認するか、管理者にお問い合わせください。"
          : null;
  return (
    <div
      role="alert"
      className={cn(
        "min-h-screen flex flex-col items-center justify-center",
        "gap-[var(--gap)] px-[var(--pad-x)] py-[var(--pad-y)]",
        "font-[var(--f-sans)]",
      )}
    >
      <h1 className="text-[length:var(--fs-lg)] font-semibold m-0">{heading}</h1>
      <p className="text-[length:var(--fs-sm)] text-[var(--ink-2)] text-center max-w-[380px] m-0">
        {message}
      </p>
      {subhint && (
        <p className="text-[length:var(--fs-xs)] text-[var(--ink-3)] text-center max-w-[380px] m-0">
          {subhint}
        </p>
      )}
      {retryCount > 0 && (
        <p className="m-0 text-[length:var(--fs-xs)] text-[var(--ink-4)]">再試行 {retryCount} 回</p>
      )}
      <Button
        type="button"
        onClick={onRetry}
        className="min-h-[var(--row-h)] px-5 text-[length:var(--fs-md)] font-bold bg-[var(--ok)] text-white hover:bg-[var(--ok)]/90"
      >
        再試行
      </Button>
    </div>
  );
}

function LoadingScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setSlow(true), 4000);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "min-h-screen flex flex-col items-center justify-center",
        "gap-4 px-[var(--pad-x)] py-[var(--pad-y)]",
        "font-[var(--f-sans)] text-[var(--ink-2)]",
      )}
    >
      {/* @keyframes spin は Tailwind animate-spin で代替できないため保持 */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div
        aria-hidden="true"
        className="w-9 h-9 rounded-full border-[3px] border-[var(--rule)] border-t-[var(--ok)]"
        style={{ animation: "spin 0.8s linear infinite" }}
      />
      <div className="text-[length:var(--fs-md)]">サインイン中…</div>
      {slow && (
        <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)] text-center max-w-[280px]">
          通信に時間がかかっています。電波状況をご確認のうえ、しばらくお待ちください。
        </div>
      )}
    </div>
  );
}
